import React from 'react';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';
import urlParse from 'url-parse';

import SubscribeToNewsletter from '@components/Newsletter/SubscribeToNewsletter';
import RelatedStoriesMidArticle from '@components/RelatedStories/MidArticle';
import MidArticleDeepResearchUnit from '@components/pages/Article/MidArticle/DeepResearchUnit';
import MidArticleEvent from '@components/pages/Article/MidArticle/Event';
import { MidArticleEventProps } from '@components/pages/Article/MidArticle/Event/interfaces';
import { Article } from '@interfaces/homepage';
import { READER_URL_PRODUCTION } from '@src/env';

const MID_ARTICLE_DEEP_RESEARCH_UNIT = 'mid_article_deep_research_unit';
const MID_ARTICLE_EVENT_UNIT = 'mid_article_event_unit';
const MID_ARTICLE_RECIRC_UNIT = 'mid_article_recirc_unit';
const NEWSLETTER_SUBSCRIBE_HTML = 'newsletter_subscribe_unit';
const BLOCKED_GENERATED_CONTENT_NODES = new Set(['iframe', 'object', 'embed']);

const transformLinksToBeExternal = (node) => {
  if (node.type === 'tag' && node.name === 'a') {
    const href = node.attribs?.href;

    // Ignore jump and mail to links
    if (href && !(href.startsWith('#') || href.startsWith('mailto:'))) {
      const url = urlParse(href, true);

      if (url.host !== urlParse(READER_URL_PRODUCTION).host) {
        // rc query params are often included in links by mistake by authors
        // copying URLs.
        if (url.query && url.query.rc) {
          delete url.query.rc;
        }

        return {
          ...node,
          attribs: {
            ...node.attribs,
            href: url.toString(),
            target: '_blank', // Set links to open in a new tab
          },
        };
      }
    }
  }

  return node;
};

const transformers = (node) => {
  return [transformLinksToBeExternal].reduce((accNode, tf) => {
    return tf(accNode);
  }, node);
};

const transform = (node, index) => convertNodeToElement(transformers(node), index, transform);

const generatedContentTransform = (node, index) => {
  if (node.type === 'tag' && BLOCKED_GENERATED_CONTENT_NODES.has(node.name)) return null;
  return convertNodeToElement(transformers(node), index, generatedContentTransform);
};

const articleTransform = (node, index, newsletterId, relatedArticles, relatedEvent, articleId) => {
  if (node?.attribs?.class?.includes('embedded-timeline') && node?.attribs?.style) {
    node.attribs.style = '';
  } else if (node.name === MID_ARTICLE_DEEP_RESEARCH_UNIT) {
    return (
      <MidArticleDeepResearchUnit key={`deep-research-module-${index}`} articleId={articleId} />
    );
  } else if (node.name === MID_ARTICLE_EVENT_UNIT && relatedEvent) {
    return <MidArticleEvent key={`mid-article-event-${index}`} event={relatedEvent} />;
  } else if (node.name === NEWSLETTER_SUBSCRIBE_HTML) {
    return <SubscribeToNewsletter key={`newsletter-subscribe-${index}`} id={newsletterId} />;
  } else if (node.name === MID_ARTICLE_RECIRC_UNIT && relatedArticles) {
    return (
      <RelatedStoriesMidArticle
        key={`related-stories-mid-article-${index}`}
        articles={relatedArticles}
      />
    );
  }
  return convertNodeToElement(transformers(node), index, articleTransform);
};

const AuthoredContentReactToHtmlParser = (text) => {
  return ReactHtmlParser(text, { transform });
};

// Splits a text node into per-word <span>s so a streaming answer can fade each
// word in as it arrives (see MarkdownBlock / the `dr-fade-word` styles).
// Whitespace runs are kept as plain text between the spans, so wrapping and
// spacing are unchanged. Keys are positional and stable as the text grows, so
// React keeps already-shown words mounted and only the newly appended word runs
// its CSS mount animation — the char-by-char reveal loop then staggers those
// mounts into a smooth word-by-word cascade.
const wrapWordsInFadeSpans = (data, keyPrefix) => {
  // split on whitespace keeping the delimiters: even indices are words
  // (possibly ''), odd indices are the whitespace runs between them.
  return data.split(/(\s+)/).map((token, i) => {
    if (i % 2 === 1 || token === '') return token;
    return (
      <span key={`${keyPrefix}-${i}`} className="dr-fade-word">
        {token}
      </span>
    );
  });
};

const convertFadeWordsNode = (node, index, recursiveTransform) => {
  if (node.type === 'text') {
    return (
      <React.Fragment key={`fw-${index}`}>
        {wrapWordsInFadeSpans(node.data ?? '', `fw-${index}`)}
      </React.Fragment>
    );
  }
  return convertNodeToElement(transformLinksToBeExternal(node), index, recursiveTransform);
};

const fadeWordsTransform = (node, index) => convertFadeWordsNode(node, index, fadeWordsTransform);

const fadeWordsGeneratedContentTransform = (node, index) => {
  if (node.type === 'tag' && BLOCKED_GENERATED_CONTENT_NODES.has(node.name)) return null;
  return convertFadeWordsNode(node, index, fadeWordsGeneratedContentTransform);
};

// Like AuthoredContentReactToHtmlParser but wraps words for the fade-in effect.
// Only used for the live streaming turn; completed/restored content uses the
// plain parser above (no extra spans, identical DOM to before).
const FadeWordsReactToHtmlParser = (text) => {
  return ReactHtmlParser(text, { transform: fadeWordsTransform });
};

// Deep Research generated content keeps authored-link and fade behavior while
// rejecting active embed nodes recursively at the HTML parser boundary.
const GeneratedContentReactToHtmlParser = (text) => {
  return ReactHtmlParser(text, { transform: generatedContentTransform });
};

const FadeWordsGeneratedContentReactToHtmlParser = (text) => {
  return ReactHtmlParser(text, { transform: fadeWordsGeneratedContentTransform });
};

const AuthoredArticleContentReactToHtmlParser = ({
  text,
  newsletterId,
  relatedArticles = [],
  relatedEvent,
  articleId,
}: {
  text: string;
  newsletterId?: string | number;
  relatedArticles?: Article[];
  relatedEvent?: MidArticleEventProps;
  articleId?: number;
}) => {
  return ReactHtmlParser(text, {
    transform: (node, index) =>
      articleTransform(node, index, newsletterId, relatedArticles, relatedEvent, articleId),
  });
};

export {
  AuthoredArticleContentReactToHtmlParser,
  AuthoredContentReactToHtmlParser,
  FadeWordsGeneratedContentReactToHtmlParser,
  FadeWordsReactToHtmlParser,
  GeneratedContentReactToHtmlParser,
};
